import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { shouldTranslateByDefault } from '@icp/settings';
import { HELPER_TEXT_TYPES, helpersIsEmpty } from '@icp/form-renderer-core';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import { useClassName, useDataSourceSupportLazy, useVariablePatternArray } from '../../hooks';
import { withFieldWrapper } from '../../fieldWrapper';
import { ConditionalPropertyPropType, SingleDataSourceTypes } from '../../propTypes';
import {
  DEFAULT_VALUE_TYPE,
  getDisplayText,
  toComponentValue,
  toInterfaceValue,
} from '../SelectElement/utils';
import { formatOptions, idToGroupKeys, legacySupportBuildTreeBy, treeFindOption } from './utils';
import { DEFAULT_MAPPING } from '../CascaderElement/utils';
import TreeSelectUI from './TreeSelectUI';

const TreeSelectElement = forwardRef(function TreeSelectElement(props, ref) {
  const {
    keyPath,
    id,
    className: classNameProp,
    title,
    value: valueProp,
    disabled,
    readonly,
    style,
    validation,
    status,
    helpers,
    onChange,
    onTouchChanged,
    fieldTitleProps,
    componentProps = {},
  } = props;

  const {
    multiple,
    valueType = DEFAULT_VALUE_TYPE,
    mapping = DEFAULT_MAPPING,
    useOriginValue = false, // TODO, TreeSelect 的 useOriginValue 好像没生效？toInterfaceValue 里没看到传
    stringEqual = false,
    dataSource,
    dataUrl,
    dataUrlResolveMode,
    dataFilters,
    sortModel,
    dataResponseKeyPath = 'results',
    transformDataResponse,
    translateDataResponse = shouldTranslateByDefault(),
    debounceTime,
    defaultValue,
    httpMethod,
    selectColId,
    needCount = false,
    treeData: treeDataProp,
    buildTreeBy: buildTreeByProp,
    dataExclusion: dataExclusionProp,
    showSearch: showSearchProp,
    treeLazyLoading = false,
    onSearch,
    ...otherComponentProps
  } = componentProps;

  const showSearch = showSearchProp !== undefined ? showSearchProp : multiple; // multiple 的时候默认开启搜索

  const ElementDecorator = useElementDecorator();
  const isInDesign = useIsInDesign();

  const className = useClassName(classNameProp);
  const classNameComp = useClassName(componentProps.className);
  const dataExclusion = useVariablePatternArray(dataExclusionProp);
  const buildTreeBy = useMemo(() => legacySupportBuildTreeBy(buildTreeByProp), [buildTreeByProp]);

  const [searchText, setSearchText] = useState('');

  const nodeRef = useRef(null);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
    }),
    [],
  );

  const formatItems = useCallback(
    (items) => {
      return formatOptions({
        options: items,
        mapping,
        stringEqual,
        buildTreeBy,
        dataExclusion,
        treeLazyLoading,
      });
    },
    [buildTreeBy, dataExclusion, mapping, stringEqual, treeLazyLoading],
  );

  const { loading, dataFetched, loadChildren } = useDataSourceSupportLazy({
    skip: isInDesign || !!treeDataProp,
    lazy: treeLazyLoading,
    searchText,
    buildTreeBy,
    cacheBlockSize: Infinity,
    dataSource,
    dataUrl,
    dataResponseKeyPath,
    dataFilters,
    sortModel,
    transformDataResponse,
    translateDataResponse,
    debounceTime,
    defaultValue,
    httpMethod,
    selectColId,
    needCount,
    formatData: formatItems,
    dataUrlResolveMode,
  });

  const options = useMemo(() => {
    // TODO，有个无意义的性能消耗是每次懒加载都要把全部数据 format 一份
    return formatOptions({
      options: treeDataProp || dataFetched,
      mapping,
      stringEqual,
      buildTreeBy,
      dataExclusion,
      treeLazyLoading,
    });
  }, [
    treeDataProp,
    dataFetched,
    mapping,
    stringEqual,
    treeLazyLoading,
    buildTreeBy,
    dataExclusion,
  ]);

  const innerValue = useMemo(() => {
    return toComponentValue({
      valueProp,
      options,
      findOption: treeFindOption,
      multiple,
      mapping,
      stringEqual,
    });
  }, [mapping, multiple, options, stringEqual, valueProp]);

  const handleChange = (newValue) => {
    const val = toInterfaceValue({
      oldValue: valueProp,
      newValue,
      options,
      findOption: treeFindOption,
      multiple,
      valueType,
      mapping,
      stringEqual,
    });
    onChange(val);
  };

  const handleSearch = (newSearchText) => {
    if (treeLazyLoading) {
      setSearchText(newSearchText);
    }
    if (onSearch) {
      onSearch(newSearchText);
    }
  };

  const handleLoadChildren = treeLazyLoading
    ? (op) => {
        const groupKeys = idToGroupKeys(options, op, buildTreeBy);
        return loadChildren({ groupKeys });
      }
    : undefined;

  // 为了避免当 valueType 是 value 的时候，fetching 过程中显示一串看不懂的 code
  const hideMeaningLessValue =
    loading &&
    innerValue &&
    (Array.isArray(innerValue) ? !innerValue[0]?.label : !innerValue.label);

  const readonlyText = useMemo(() => {
    if (!readonly) {
      return '';
    }

    return getDisplayText({
      valueProp: innerValue,
      options,
      mapping,
      useOriginValue,
      findOption: treeFindOption,
      stringEqual,
      hideMeaningLessValue,
    });
  }, [hideMeaningLessValue, mapping, options, readonly, stringEqual, useOriginValue, innerValue]);

  return (
    <ElementDecorator keyPath={keyPath} id={id}>
      <div
        className={clsx(
          'tree-select-element',
          'input-element',
          'form-element',
          {
            'has-helper': !helpersIsEmpty(helpers),
          },
          className,
        )}
        style={style}
        ref={ref}
      >
        <TreeSelectUI
          title={title}
          validation={validation}
          disabled={disabled}
          readonly={readonly}
          status={status}
          helpers={helpers}
          value={(() => {
            if (hideMeaningLessValue) {
              return multiple ? [] : {};
            }
            return innerValue;
          })()}
          onChange={handleChange}
          onTouchChanged={onTouchChanged}
          componentProps={{
            ...otherComponentProps,
            className: classNameComp,
          }}
          fieldTitleProps={fieldTitleProps}
          multiple={multiple}
          mapping={mapping}
          readonlyText={readonlyText}
          loading={loading}
          options={loading ? [] : options}
          treeLazyLoading={treeLazyLoading}
          loadData={handleLoadChildren}
          showSearch={showSearch}
          onSearch={showSearch ? handleSearch : undefined}
        />
      </div>
    </ElementDecorator>
  );
});

TreeSelectElement.propTypes = {
  keyPath: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.bool,
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
    }),
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool])),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
      }),
    ),
  ]),
  componentProps: PropTypes.shape({
    /**
     * 组件的 className
     */
    className: PropTypes.string,
    /**
     * 组件的 style
     */
    style: PropTypes.shape({}),
    /**
     * Describes the value is a `childEnum` item or a `childEnum` item's value,
     * describes whether the value is always an array no matter mode is single or multiple.
     *
     * example:
     *
     * Given childEnum: `[{ value: 1, label: 'AAA' }, { value: 'b', label: 'BBB' }]`
     *
     * When valueType is `value`, value would be `1` or `'b'` in `single` mode, `[1]` or `[1, 'b']` in `multiple` mode.
     *
     * When valueType is `item`, value would be `{ value: 1, label: 'AAA' }`  in `single` mode, `[{ value: 1, label: 'AAA' }, {...}]` in `multiple` mode.
     *
     * When valueType is `valueAlwaysArray`, value would be `[1]` in `single` mode, `[1, 'b']` in `multiple` mode.
     *
     * When valueType is `itemAlwaysArray`, value would be `[{ value: 1, label: 'AAA' }]` in `single` mode, `[{...}, {...}]` in `multiple` mode.
     */
    valueType: PropTypes.oneOf(['value', 'item', 'valueAlwaysArray', 'itemAlwaysArray']),
    /**
     * value 的映射关系，默认是 { value, label }
     */
    mapping: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      // Other key map to save
    }),
    ...SingleDataSourceTypes,
    /**
     * antd TreeSelect 元素的 treeData
     */
    treeData: PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        label: PropTypes.string,
        children: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    ),
    /**
     * 是否多选
     */
    multiple: PropTypes.bool,
    /**
     * 在 api 返回的数据中排除某些数据
     */
    dataExclusion: PropTypes.arrayOf(PropTypes.string),
  }),
  fieldTitleProps: PropTypes.shape({
    showColon: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    required: PropTypes.bool,
  }),
  disabled: ConditionalPropertyPropType(PropTypes.bool),
  readonly: ConditionalPropertyPropType(PropTypes.bool),
  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
};

// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
TreeSelectElement.displayName = 'TreeSelect';

export default withFieldWrapper(TreeSelectElement);
